import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import * as Bananas from "bananas-commerce-admin";

import {
  Clear,
  Done,
  GridView as GridViewIcon,
  HourglassEmpty as HourglassEmptyIcon,
} from "@mui/icons-material";
import { Button } from "@mui/material";
import { Box } from "@mui/system";

import ErrorCard from "@/extensions/klarahill/components/ErrorCard";
import SearchBar from "@/extensions/klarahill/components/SearchBar";
import { AdStatus } from "@/extensions/obituary/types";

import OrdersList from "../../components/OrdersList";
import { ObituaryMediaResponse } from "../../types";

const OrderListPage: Bananas.types.PageComponent<ObituaryMediaResponse[]> = ({
  data,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedTab = searchParams.get("panel_filter") ?? "all";

  const filteredData = useMemo(() => {
    if (selectedTab === "all") {
      return data;
    }

    const statusMap: Record<string, AdStatus> = {
      pending: "pending_mediahouse_review",
      approved: "sent",
      rejected: "rejected",
    };

    return data?.filter(
      (item) =>
        item.status === statusMap[selectedTab as keyof typeof statusMap],
    );
  }, [data, selectedTab]);

  return (
    <Bananas.Page defaultTab="all" tabSearchParam="panel_filter">
      <Bananas.Header variant="opaque">
        <Bananas.TitleBar title="Ordrar">
          <SearchBar
            defaultValue={searchParams.get("order_id") ?? ""}
            placeholder="Sök efter ordernummer"
            slotProps={{
              htmlInput: { pattern: "[0-9]*", inputMode: "numeric" },
            }}
            sx={{
              bgcolor: "action.hover",
              borderRadius: "8px",
              "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                {
                  WebkitAppearance: "none",
                  margin: 0,
                },
              "& input[type=number]": {
                MozAppearance: "textfield",
              },
            }}
            type="number"
            onSubmit={(value) => {
              // Only accept integers
              const trimmedValue = value.trim();
              if (!trimmedValue || /^\d+$/.test(trimmedValue)) {
                setSearchParams((sp) => {
                  const newParams = { ...Object.fromEntries(sp.entries()) };
                  if (trimmedValue) {
                    newParams.order_id = trimmedValue;
                  } else {
                    delete newParams.order_id;
                  }
                  return newParams;
                });
              }
            }}
          />
        </Bananas.TitleBar>

        <Bananas.Tabs
          aria-label="Kontrollera vilka begravningsärendent som syns."
          onChange={(tab) => {
            setSearchParams((sp) => ({
              ...Object.fromEntries(sp.entries()),
              panel_filter: tab,
            }));
          }}
        >
          <Bananas.Tab
            key="all"
            icon={<GridViewIcon />}
            label="Alla"
            value="all"
          />
          <Bananas.Tab
            key="pending"
            icon={<HourglassEmptyIcon />}
            label="Väntar på granskning"
            value="pending"
          />
          <Bananas.Tab
            key="approved"
            icon={<Done />}
            label="Godkänd"
            value="approved"
          />
          <Bananas.Tab
            key="rejected"
            icon={<Clear />}
            label="Refuserad"
            value="rejected"
          />
        </Bananas.Tabs>
      </Bananas.Header>

      {filteredData != null && filteredData?.length !== 0 ? (
        <Bananas.Content layout="fullWidth">
          <OrdersList data={filteredData} />
        </Bananas.Content>
      ) : (
        <Box
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            alignItems: "center",
          }}
        >
          <ErrorCard errorMessage="Hittade inga ordrar för ditt urval." />
          <Button
            size="medium"
            style={{ width: "max-content" }}
            variant="outlined"
            onClick={() => setSearchParams({})}
          >
            Återställ filter
          </Button>
        </Box>
      )}
    </Bananas.Page>
  );
};

export default OrderListPage;
